import { BitkitNoteCard, IconClock, IconFlag, IconHashtag, IconStack } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { useContext, useEffect, useLayoutEffect, useState } from 'react';

import { useBuildCacheAddonTrial } from '@/client/BFF/Organizations/AddonTrials/useBuildCacheAddonTrial';
import MacAppPromoBanner, { useMacAppPromoBanner } from '@/components/banners/MacAppPromoBanner/MacAppPromoBanner';
import PullRequestOrCommitHashLink from '@/components/BuildListItem/PullRequestOrCommitHashLink/PullRequestOrCommitHashLink';
import {
  holdOnboardingChecklist,
  releaseOnboardingChecklist,
} from '@/components/OnboardingChecklist/onboardingChecklistGate';
import ContentWrapper from '@/components/page/ContentWrapper';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import ProtectedPage from '@/components/page/ProtectedPage';
import PublicBuildPage from '@/components/page/PublicBuildPage/PublicBuildPage';
import CacheMeta from '@/components/RunnableSummaryCard/CacheMeta';
import CreditsMeta from '@/components/RunnableSummaryCard/CreditsMeta';
import DurationMeta from '@/components/RunnableSummaryCard/DurationMeta';
import RunnableMeta from '@/components/RunnableSummaryCard/RunnableMeta';
import RunnableSummaryCard from '@/components/RunnableSummaryCard/RunnableSummaryCard';
import BuildStatusTag from '@/components/StatusTag/BuildStatusTag';
import { BuildDetailsTabId } from '@/enums/BuildDetailsTabs';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import useQueryParams from '@/hooks/useQueryParams';
import { WorkspaceAccount } from '@/models/Account';
import { viewedAsPublicPage } from '@/models/App';
import { getBuildStatusColors, getTriggerTypeIcon, sourceBranch, targetBranch } from '@/models/Build';
import BuildStatus from '@/models/BuildStatus';
import { getGitProviderCommitHashUrl, getGitProviderPullRequestUrl } from '@/models/GitProvider';
import { GlobalProps } from '@/models/GlobalProps';
import useBuildDetailsPageStore, { updateBuild } from '@/pages/BuildDetailsPage/BuildDetailsPage.store';
import BuildAlerts from '@/pages/BuildDetailsPage/components/BuildAlerts/BuildAlerts';
import { getDefaultTab } from '@/pages/BuildDetailsPage/components/BuildDetailsTabs/utils/BuildDetailsTabs.utils';
import BuildHeader from '@/pages/BuildDetailsPage/components/BuildHeader/BuildHeader';
import RemoteAccessInstructions from '@/pages/BuildDetailsPage/components/RemoteAccessInstructions/RemoteAccessInstructions';
import useUpdateFavicon from '@/pages/BuildDetailsPage/hooks/useUpdateFavicon';
import isRemoteAccessInstructionsVisible from '@/pages/BuildDetailsPage/utils/isRemoteAccessInstructionsVisible';
import { formatDate } from '@/utils/commonFormats';
import { updateQueryParam } from '@/utils/queryParams';

import AIConfigSignpost from './components/AIConfigSignpost/AIConfigSignpost';
import BuildAnnotations from './components/BuildAnnotations/BuildAnnotations';
import BuildDetailsTabs from './components/BuildDetailsTabs/BuildDetailsTabs';
import DeprecatedMachineBanner from './components/DeprecatedMachineBanner/DeprecatedMachineBanner';
import LastSuccessfulBuild from './components/LastSuccessfulBuild/LastSuccessfulBuild';
import useBuildDetails, { useBuildProgress } from './hooks/useBuildDetails';
import useInsightsDetails from './hooks/useInsightsDetails';
import { BuildDetailsPageProps } from './types/BuildDetailsPageProps';
import { buildMapperInstance } from './utils/BuildDetailsDataMapper';

const BuildDetailsContent = () => {
  const { account, user } = useContext(GlobalPropsContext) as GlobalProps;
  const { trial: buildCacheTrial } = useBuildCacheAddonTrial(account?.slug ?? '');

  const {
    app,
    build,
    initBuild,
    abilities,
    stack,
    machineType,
    cacheInfo,
    remoteAccessData,
    isViewedAsPublicPage,
    traceToken,
  } = useBuildDetailsPageStore();

  const queryParams = useQueryParams();
  const macAppPromoBanner = useMacAppPromoBanner('build_details');
  const { build: fetchedBuild } = useBuildDetails(app.slug, build.slug, isViewedAsPublicPage, initBuild);
  const { progress, previousBuildDuration } = useBuildProgress(app.slug, isViewedAsPublicPage);

  const machineDeprecationData = account?.useReplacementForDeprecatedMachines;
  const deprecatedMachineIds = ['standard', 'elite', 'elite-xl'];
  const showDeprecatedMachineBanner =
    !isViewedAsPublicPage &&
    account &&
    machineType &&
    (deprecatedMachineIds.includes(machineType.id) || machineType.replacedDueToDeprecation === 'wave') &&
    machineDeprecationData;

  const shouldShowSpeedUpBuildAlert =
    build.status === BuildStatus.Success &&
    !cacheInfo?.usesCache &&
    !!cacheInfo?.cacheCandidate &&
    !cacheInfo?.hasBaselineInvocation;

  useEffect(() => {
    if (fetchedBuild) {
      updateBuild(fetchedBuild);
    }
  }, [fetchedBuild]);

  useUpdateFavicon(build.status);

  const isFailed = build.status === BuildStatus.Error;
  useEffect(() => {
    if (!isFailed) {
      return undefined;
    }
    holdOnboardingChecklist('failed_build');
    return () => releaseOnboardingChecklist('failed_build');
  }, [isFailed]);

  const insightsDetails = useInsightsDetails({
    accountSlug: account?.slug,
    projectId: app.projectId || '',
    projectSlug: app.slug,
    triggeredWorkflow: build.triggeredWorkflow,
    traceToken,
  });

  const gitProvider = build.repository?.provider || app.provider;
  const gitOwner = build.repository?.repoOwner || app.gitOwner;
  const gitRepoSlug = build.repository?.repoSlug || app.gitRepoSlug;
  const commitHashUrl = getGitProviderCommitHashUrl(gitProvider, gitOwner, gitRepoSlug, build.commitHash);
  const pullRequestUrl = getGitProviderPullRequestUrl(gitProvider, gitOwner, gitRepoSlug, build.pullRequestId);

  const priority = build.originalBuildParams?.priority || build.triggerParams?.priority;

  const isAccountPrivateOrDedicatedCloud = account && (account as WorkspaceAccount).isPrivateOrDedicatedCloud;
  const stackDeprecationDate = stack.deprecatedFrom ? formatDate(stack.deprecatedFrom, true) : undefined;

  let stackAndMachineWarning = '';
  if (stackDeprecationDate && !isAccountPrivateOrDedicatedCloud) {
    stackAndMachineWarning = `Change your build stack, or your builds will not work after ${stackDeprecationDate}`;
  } else if (build.stackRollbackVersion) {
    stackAndMachineWarning = 'Builds using a rollback stack version start slower.';
  }

  return (
    <>
      <BuildHeader />
      <ContentWrapper paddingBlockStart={['16', '32']}>
        <BuildAlerts
          hasPaymentProblems={!!account?.hasPaymentProblems}
          isOwner={user?.slug === app.owner?.slug}
          isAccountPrivateOrDedicatedCloud={isAccountPrivateOrDedicatedCloud}
          stackDeprecationDate={stackDeprecationDate}
        />
        <AIConfigSignpost />
        {isRemoteAccessInstructionsVisible(build, remoteAccessData) && remoteAccessData && (
          <RemoteAccessInstructions
            buildSlug={build.slug}
            appSlug={app.slug}
            isBuildOnHold={build.isOnHold}
            remoteAccessData={remoteAccessData}
          />
        )}
        {/*
         * minWidth is needed to text-overflow: ellipsis to work inside a flex layout
         * Check this article for explanation: https://css-tricks.com/flexbox-truncated-text/
         */}
        <Box display="flex" flexDirection="column" gap={['16', '24']} minWidth={0} marginBottom={['16', '24']}>
          {!isViewedAsPublicPage && macAppPromoBanner.isVisible && (
            <MacAppPromoBanner
              onDismiss={macAppPromoBanner.onDismiss}
              onDownloadClick={() =>
                segmentTrack('Bitrise Desktop App Download Attempted', {
                  workspace_slug: account?.slug,
                  app_slug: app.slug,
                  platform: 'website',
                  source: 'build_details_page',
                })
              }
              title="See your builds without the browser tab"
              message="Our Mac app watches the current branch of your local repo from the menu bar - so you know when your PR is green without opening Bitrise."
            />
          )}
          <RunnableSummaryCard
            abortReason={build.abortReason || undefined}
            bottlenecksCount={insightsDetails.bottlenecksCount}
            bottlenecksUrl={insightsDetails.bottlenecksUrl}
            commitMessage={build.commitMessage}
            datadogName="buildStatus"
            datadogPage="buildDetails_v2"
            footerLeftSlot={
              <>
                <RunnableMeta
                  icon={IconClock}
                  data={buildMapperInstance.getTriggeredAt(build)}
                  defaultTooltipLabel="Triggered at"
                />
                <RunnableMeta
                  icon={IconStack}
                  data={machineType ? `${stack.displayName} • ${machineType.displayName}` : stack.displayName}
                  status={stackAndMachineWarning ? 'warning' : undefined}
                  defaultTooltipLabel={
                    <>
                      <Text as="span" fontWeight="600">
                        {machineType ? 'Stack & machine' : 'Stack'}
                      </Text>
                      {machineType?.specs && (
                        <>
                          <br />
                          {machineType.specs}
                        </>
                      )}
                    </>
                  }
                  definitionText={stackAndMachineWarning ? `Stack & machine: ${stackAndMachineWarning}` : undefined}
                />
                {priority !== undefined && (
                  <RunnableMeta
                    icon={IconFlag}
                    data={`${priority} (${priority > 0 ? 'High' : 'Low'})`}
                    defaultTooltipLabel="Build priority"
                  />
                )}
              </>
            }
            footerRightSlot={
              <>
                <DurationMeta
                  duration={buildMapperInstance.getDuration(build)}
                  previousDuration={previousBuildDuration}
                  insightsDetails={build.status !== BuildStatus.Running ? insightsDetails : undefined}
                />
                <CacheMeta
                  appSlug={app.slug}
                  buildCacheInfo={cacheInfo}
                  buildSlug={build.slug}
                  showHint={!!account && !!(account.hasBuildCacheSubscription || buildCacheTrial?.isActive)}
                />
                <CreditsMeta
                  data={account?.isCreditBasedPlan ? buildMapperInstance.getCreditCost(build, true) : undefined}
                  insightsDetails={build.status !== BuildStatus.Running ? insightsDetails : undefined}
                />
                <RunnableMeta icon={IconHashtag} data={build.buildNumber} defaultTooltipLabel="Build number" />
              </>
            }
            headerRightSlot={
              <LastSuccessfulBuild build={build} isViewedAsPublicPage={isViewedAsPublicPage} projectSlug={app.slug} />
            }
            pullRequestOrCommitHashLink={
              <PullRequestOrCommitHashLink
                size="3"
                gitProvider={build.repository?.provider || app.provider}
                pullRequestId={build.pullRequestId}
                pullRequestUrl={build.pullRequestViewUrl || pullRequestUrl}
                commitHash={build.commitHash}
                commitHashUrl={build.commitViewUrl || commitHashUrl}
              />
            }
            runnableName={build.triggeredWorkflow}
            sourceBranch={sourceBranch(build)}
            statusColor={getBuildStatusColors(build).color}
            statusTag={<BuildStatusTag build={build} fixedWidth={false} progress={progress} size="sm" />}
            tag={build.tag}
            targetBranch={targetBranch(build)}
            triggeredBy={buildMapperInstance.getTriggeredBy(build)}
            triggerTypeIcon={getTriggerTypeIcon({ tag: build.tag, pullRequestId: build.pullRequestId })}
          />
          {showDeprecatedMachineBanner && (
            <DeprecatedMachineBanner
              workspaceSlug={account.slug}
              appSlug={app.slug}
              canEditWorkflow={abilities.canChangeSettings}
              canChangeWorkspaceSettings={account.isOwner}
              isGracePeriodRequested={!!account.isDeprecatedMachineGracePeriodRequested}
              gracePeriodStartedAt={new Date(machineDeprecationData.gracePeriodStartedAt)}
              gracePeriodEndedAt={new Date(machineDeprecationData.gracePeriodEndedAt)}
            />
          )}
          {shouldShowSpeedUpBuildAlert && (
            <BitkitNoteCard
              message="Optimize Gradle Steps with Build Cache to improve the build duration."
              status="warning"
              title="You can speed up the build"
              action={{
                label: 'Go to Build cache',
                onClick: () => {
                  segmentTrack('Speed Up Build With Caching Notification Go To Build Cache Button Clicked', {
                    project_id: app.projectId,
                    project_title: app.title,
                    build_slug: build.slug,
                    tab_name: queryParams.get('tab') || getDefaultTab(abilities.canViewBuildLogs),
                  });

                  updateQueryParam('tab', [BuildDetailsTabId.buildCache]);
                },
              }}
            />
          )}

          <BuildAnnotations />
          <BuildDetailsTabs canSpeedUpBuild={shouldShowSpeedUpBuildAlert} />
        </Box>
      </ContentWrapper>
    </>
  );
};

const BuildDetailsPage = (pageProps: BuildDetailsPageProps) => {
  const { user } = useContext(GlobalPropsContext);
  const [isInitialized, setIsInitialized] = useState(false);
  const isViewedAsPublicPage = viewedAsPublicPage(pageProps.app.isPublic, user);
  const canViewAddonsAndLoggedIn = pageProps.abilities.canViewAddons && !!user?.username;

  useLayoutEffect(() => {
    if (!isInitialized) {
      useBuildDetailsPageStore.setState({
        ...pageProps,
        initBuild: pageProps.build,
        isViewedAsPublicPage,
        canViewAddonsAndLoggedIn,
      });
      setIsInitialized(true);
    }
  }, [canViewAddonsAndLoggedIn, isInitialized, isViewedAsPublicPage, pageProps]);

  if (!isInitialized) {
    return null;
  }

  if (isViewedAsPublicPage) {
    return (
      <PublicBuildPage>
        <BuildDetailsContent />
      </PublicBuildPage>
    );
  }

  return (
    <ProtectedPage showLimitedTrialBanner>
      <BuildDetailsContent />
    </ProtectedPage>
  );
};

export default BuildDetailsPage;
